import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { usePdfPageThumbnails } from '@/features/pdf-page-preview'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  cropPdf,
  croppedFilename,
  formatMm,
  inspectPdfForCrop,
  normalizeMargins,
  validateCropMargins,
  validateFileSize,
  type CropInspection,
  type CropMarginsMm,
  type CropValidationResult,
  type PageDimensions,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('crop-pdf').analyticsKey
const DEFAULT_MARGINS: CropMarginsMm = { top: 0, right: 0, bottom: 0, left: 0 }
const MIN_PREVIEW_SIDE_MM = 1

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading'; file: File }
  | { kind: 'ready'; file: File; inspection: CropInspection }
  | { kind: 'loading'; file: File; inspection: CropInspection }
  | {
      kind: 'success'
      file: File
      blob: Blob
      objectUrl: string
      pageCount: number
      margins: CropMarginsMm
      firstPage: PageDimensions
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

type CropHandle = 'top' | 'right' | 'bottom' | 'left'

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

interface FileSummaryProps {
  file: File
  inspection: CropInspection
  onClear: () => void
  disabled: boolean
}

function FileSummary({ file, inspection, onClear, disabled }: FileSummaryProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatBytes(file.size)} · {format(t.cropPdf.pageCount, { pages: inspection.pageCount })}
        </p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

interface CropPreviewProps {
  file: File
  inspection: CropInspection
  margins: CropMarginsMm
  onChange: (margins: CropMarginsMm) => void
  disabled: boolean
}

function CropPreview({ file, inspection, margins, onChange, disabled }: CropPreviewProps) {
  const t = useT()
  const previewRef = useRef<HTMLDivElement>(null)
  const [activeHandle, setActiveHandle] = useState<CropHandle | null>(null)
  const pagePreview = usePdfPageThumbnails({
    file,
    pageNumbers: [1],
    maxPages: 1,
    targetWidth: 320,
  })
  const firstPageThumbnail = pagePreview.thumbnails[0]
  const previewMargins = clampMarginsToPage(margins, inspection.firstPage)
  const cropStyle = {
    top: `${(previewMargins.top / inspection.firstPage.heightMm) * 100}%`,
    right: `${(previewMargins.right / inspection.firstPage.widthMm) * 100}%`,
    bottom: `${(previewMargins.bottom / inspection.firstPage.heightMm) * 100}%`,
    left: `${(previewMargins.left / inspection.firstPage.widthMm) * 100}%`,
  }

  useEffect(() => {
    if (!activeHandle || disabled) return undefined

    function handleMove(event: PointerEvent) {
      const rect = previewRef.current?.getBoundingClientRect()
      if (!rect) return

      const x = clamp(event.clientX - rect.left, 0, rect.width)
      const y = clamp(event.clientY - rect.top, 0, rect.height)
      const next = { ...margins }

      if (activeHandle === 'left') {
        next.left = clamp(
          (x / rect.width) * inspection.firstPage.widthMm,
          0,
          inspection.firstPage.widthMm - next.right - MIN_PREVIEW_SIDE_MM,
        )
      } else if (activeHandle === 'right') {
        next.right = clamp(
          ((rect.width - x) / rect.width) * inspection.firstPage.widthMm,
          0,
          inspection.firstPage.widthMm - next.left - MIN_PREVIEW_SIDE_MM,
        )
      } else if (activeHandle === 'top') {
        next.top = clamp(
          (y / rect.height) * inspection.firstPage.heightMm,
          0,
          inspection.firstPage.heightMm - next.bottom - MIN_PREVIEW_SIDE_MM,
        )
      } else {
        next.bottom = clamp(
          ((rect.height - y) / rect.height) * inspection.firstPage.heightMm,
          0,
          inspection.firstPage.heightMm - next.top - MIN_PREVIEW_SIDE_MM,
        )
      }

      onChange(roundMargins(next))
    }

    function handleUp() {
      setActiveHandle(null)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
    }
  }, [activeHandle, disabled, inspection.firstPage.heightMm, inspection.firstPage.widthMm, margins, onChange])

  function startDrag(handle: CropHandle) {
    if (!disabled) setActiveHandle(handle)
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">{t.cropPdf.previewLabel}</p>
      <div
        ref={previewRef}
        className={cn(
          'relative mx-auto w-full max-w-[300px] overflow-hidden rounded-lg border bg-background shadow-inner',
          disabled && 'opacity-60',
        )}
        style={{ aspectRatio: `${inspection.firstPage.width} / ${inspection.firstPage.height}` }}
      >
        {firstPageThumbnail ? (
          <img
            src={firstPageThumbnail.dataUrl}
            alt={format(t.pdfPreview.pageAlt, { page: 1 })}
            className="absolute inset-0 h-full w-full object-fill"
            loading="lazy"
          />
        ) : (
          <>
            <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--muted))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--muted))_1px,transparent_1px)] bg-[size:24px_24px]" />
            <div className="absolute inset-0 bg-muted/35" />
          </>
        )}
        <div
          className="absolute rounded border-2 border-primary bg-transparent shadow-[0_0_0_999px_hsl(var(--background)/0.62)]"
          style={cropStyle}
        >
          <button
            type="button"
            aria-label={t.cropPdf.dragTop}
            onPointerDown={() => startDrag('top')}
            disabled={disabled}
            className="absolute -top-2 left-1/2 h-4 w-12 -translate-x-1/2 cursor-ns-resize rounded-full border bg-primary"
          />
          <button
            type="button"
            aria-label={t.cropPdf.dragRight}
            onPointerDown={() => startDrag('right')}
            disabled={disabled}
            className="absolute -right-2 top-1/2 h-12 w-4 -translate-y-1/2 cursor-ew-resize rounded-full border bg-primary"
          />
          <button
            type="button"
            aria-label={t.cropPdf.dragBottom}
            onPointerDown={() => startDrag('bottom')}
            disabled={disabled}
            className="absolute -bottom-2 left-1/2 h-4 w-12 -translate-x-1/2 cursor-ns-resize rounded-full border bg-primary"
          />
          <button
            type="button"
            aria-label={t.cropPdf.dragLeft}
            onPointerDown={() => startDrag('left')}
            disabled={disabled}
            className="absolute -left-2 top-1/2 h-12 w-4 -translate-y-1/2 cursor-ew-resize rounded-full border bg-primary"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t.cropPdf.previewHint}</p>
    </div>
  )
}

interface MarginInputProps {
  id: string
  label: string
  value: number
  onChange: (value: number) => void
  disabled: boolean
}

function MarginInput({ id, label, value, onChange, disabled }: MarginInputProps) {
  return (
    <label htmlFor={id} className="space-y-1.5">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <input
        id={id}
        type="number"
        min="0"
        step="1"
        inputMode="decimal"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className={cn(
          'block w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          disabled && 'pointer-events-none opacity-50',
        )}
      />
    </label>
  )
}

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  pageCount: number
  margins: CropMarginsMm
  firstPage: PageDimensions
  onReset: () => void
}

function ResultPanel({
  file,
  blob,
  objectUrl,
  pageCount,
  margins,
  firstPage,
  onReset,
}: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = croppedFilename(file.name)
    a.click()
  }

  const cropWidth = Math.max(0, firstPage.widthMm - margins.left - margins.right)
  const cropHeight = Math.max(0, firstPage.heightMm - margins.top - margins.bottom)

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.cropPdf.pagesProcessed}</span>
            <span className="font-medium text-foreground">{pageCount}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.cropPdf.firstPageCrop}</span>
            <span className="font-medium text-foreground">
              {format(t.cropPdf.dimensionValue, {
                width: formatMm(cropWidth),
                height: formatMm(cropHeight),
              })}
            </span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.cropPdf.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export function ObrezatPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [margins, setMargins] = useState<CropMarginsMm>(DEFAULT_MARGINS)

  const isBusy = phase.kind === 'reading' || phase.kind === 'loading'
  const readyPhase =
    phase.kind === 'ready' || phase.kind === 'loading' ? phase : null
  const validation = readyPhase
    ? validateCropMargins(margins, readyPhase.inspection)
    : null
  const validationMessage =
    validation && !validation.ok ? validationErrorMessage(validation, t.cropPdf) : null

  function cleanupSuccessUrl() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
  }

  async function handleFile(file: File) {
    cleanupSuccessUrl()

    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    setMargins(DEFAULT_MARGINS)
    setPhase({ kind: 'reading', file })

    try {
      const inspection = await inspectPdfForCrop(file)
      setPhase({ kind: 'ready', file, inspection })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      toolError(UTILITY, code)
      setPhase({
        kind: 'error',
        file,
        errorCode: code,
        message: code === 'encrypted' ? t.cropPdf.encrypted : t.cropPdf.parseError,
      })
    }
  }

  function handleClear() {
    cleanupSuccessUrl()
    setMargins(DEFAULT_MARGINS)
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      void handleFile(phase.file)
      return
    }
    handleClear()
  }

  function updateMargin(side: keyof CropMarginsMm, value: number) {
    setMargins((current) => normalizeMargins({ ...current, [side]: value }))
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const cropValidation = validateCropMargins(margins, phase.inspection)
    if (!cropValidation.ok) {
      toolError(UTILITY, cropValidation.code, { total_pages: phase.inspection.pageCount })
      return
    }

    const file = phase.file
    const normalizedMargins = normalizeMargins(margins)
    setPhase({ kind: 'loading', file, inspection: phase.inspection })
    toolInvoke(UTILITY, {
      input_bytes: file.size,
      total_pages: phase.inspection.pageCount,
      margin_top_mm: normalizedMargins.top,
      margin_right_mm: normalizedMargins.right,
      margin_bottom_mm: normalizedMargins.bottom,
      margin_left_mm: normalizedMargins.left,
    })

    const startMs = Date.now()

    try {
      const { blob, pageCount } = await cropPdf(file, normalizedMargins)
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, durationMs, {
        input_bytes: file.size,
        output_bytes: blob.size,
        total_pages: pageCount,
        margin_top_mm: normalizedMargins.top,
        margin_right_mm: normalizedMargins.right,
        margin_bottom_mm: normalizedMargins.bottom,
        margin_left_mm: normalizedMargins.left,
      })

      setPhase({
        kind: 'success',
        file,
        blob,
        objectUrl,
        pageCount,
        margins: normalizedMargins,
        firstPage: phase.inspection.firstPage,
      })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      toolError(UTILITY, code, { total_pages: phase.inspection.pageCount })
      setPhase({
        kind: 'error',
        file,
        errorCode: code,
        message: code === 'encrypted' ? t.cropPdf.encrypted : t.cropPdf.parseError,
      })
    }
  }

  if (phase.kind === 'success') {
    return (
      <ResultPanel
        file={phase.file}
        blob={phase.blob}
        objectUrl={phase.objectUrl}
        pageCount={phase.pageCount}
        margins={phase.margins}
        firstPage={phase.firstPage}
        onReset={handleClear}
      />
    )
  }

  return (
    <div className="space-y-5">
      {readyPhase ? (
        <div className="space-y-4">
          <FileSummary
            file={readyPhase.file}
            inspection={readyPhase.inspection}
            onClear={handleClear}
            disabled={isBusy}
          />

          <CropPreview
            file={readyPhase.file}
            inspection={readyPhase.inspection}
            margins={margins}
            onChange={setMargins}
            disabled={isBusy}
          />

          <fieldset disabled={isBusy} className="space-y-3">
            <legend className="text-sm font-medium text-foreground">{t.cropPdf.marginsLabel}</legend>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <MarginInput
                id="crop-pdf-top"
                label={t.cropPdf.marginTop}
                value={margins.top}
                onChange={(value) => updateMargin('top', value)}
                disabled={isBusy}
              />
              <MarginInput
                id="crop-pdf-right"
                label={t.cropPdf.marginRight}
                value={margins.right}
                onChange={(value) => updateMargin('right', value)}
                disabled={isBusy}
              />
              <MarginInput
                id="crop-pdf-bottom"
                label={t.cropPdf.marginBottom}
                value={margins.bottom}
                onChange={(value) => updateMargin('bottom', value)}
                disabled={isBusy}
              />
              <MarginInput
                id="crop-pdf-left"
                label={t.cropPdf.marginLeft}
                value={margins.left}
                onChange={(value) => updateMargin('left', value)}
                disabled={isBusy}
              />
            </div>
            <p className="text-xs text-muted-foreground">{t.cropPdf.marginsHint}</p>
          </fieldset>

          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="space-y-1.5 p-4 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">{t.cropPdf.firstPageSize}</span>
                <span className="font-medium text-foreground">
                  {format(t.cropPdf.dimensionValue, {
                    width: formatMm(readyPhase.inspection.firstPage.widthMm),
                    height: formatMm(readyPhase.inspection.firstPage.heightMm),
                  })}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-muted-foreground">{t.cropPdf.minPageSize}</span>
                <span className="font-medium text-foreground">
                  {format(t.cropPdf.dimensionValue, {
                    width: formatMm(readyPhase.inspection.minPage.widthMm),
                    height: formatMm(readyPhase.inspection.minPage.heightMm),
                  })}
                </span>
              </div>
            </CardContent>
          </Card>

          {validationMessage && (
            <p className="text-sm text-destructive">{validationMessage}</p>
          )}
        </div>
      ) : (
        <DropZone onFile={handleFile} disabled={isBusy} />
      )}

      {phase.kind === 'reading' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {t.cropPdf.reading}
        </p>
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="sticky bottom-3 z-10 w-full shadow-lg"
        disabled={phase.kind !== 'ready' || !validation?.ok || isBusy}
        onClick={handleSubmit}
      >
        {phase.kind === 'loading' ? (
          <>
            <Spinner />
            {t.cropPdf.loading}
          </>
        ) : (
          t.cropPdf.submit
        )}
      </Button>
    </div>
  )
}

function validationErrorMessage(
  validation: Exclude<CropValidationResult, { ok: true }>,
  copy: ReturnType<typeof useT>['cropPdf'],
): string {
  if (validation.code === 'empty') return copy.emptyCrop
  if (validation.code === 'too_large') return copy.tooLargeCrop
  return copy.invalidMargins
}

function clampMarginsToPage(margins: CropMarginsMm, page: PageDimensions): CropMarginsMm {
  const normalized = normalizeMargins(margins)
  const left = clamp(normalized.left, 0, page.widthMm - MIN_PREVIEW_SIDE_MM)
  const right = clamp(normalized.right, 0, page.widthMm - left - MIN_PREVIEW_SIDE_MM)
  const top = clamp(normalized.top, 0, page.heightMm - MIN_PREVIEW_SIDE_MM)
  const bottom = clamp(normalized.bottom, 0, page.heightMm - top - MIN_PREVIEW_SIDE_MM)
  return { top, right, bottom, left }
}

function roundMargins(margins: CropMarginsMm): CropMarginsMm {
  return {
    top: Math.round(margins.top),
    right: Math.round(margins.right),
    bottom: Math.round(margins.bottom),
    left: Math.round(margins.left),
  }
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min
  return Math.min(max, Math.max(min, value))
}
